import type {Abi, Hex} from 'viem';
import {
    BaseError,
    ContractFunctionRevertedError,
    decodeErrorResult,
    decodeFunctionData,
    toFunctionSelector,
} from 'viem';
import appConfig from '../../../config/app-config';
import {isDevOrCiEnv} from '../../../services/config.service';

/**
 * Standard JSON-RPC error code for "execution reverted".
 * @see https://eips.ethereum.org/EIPS/eip-1474
 */
export const RPC_EXECUTION_REVERTED_CODE = 3;

/** ABI error item. */
type AbiErrorItem = Extract<Abi[number], { type: 'error' }>;

export interface ParsedReverDataViem {
    interfaceName: string;
    name: string;
    args: readonly unknown[];
    argsMap: Record<string, any> | undefined;
    selector: `0x${string}`;
}

interface ContractRec {
    name: string;
    abi: Abi;
}

let allContractRecs: ContractRec[];
let allAbisPromise: Promise<Abi> | undefined;
/** error selector -> contract: fallback for shortened revert data. */
let errorSelectorIndex: Map<Hex, { interfaceName: string; name: string }>;

// Idempotent initialization (one shared Promise instead of "if (allAbis) return").
let decoderInit: Promise<void> | undefined;

const initErrorDecoder = (): Promise<void> =>
    (decoderInit ??= (async (): Promise<void> => {
        // Load only error fragments via a separate, lightweight package entry.
        const {errorAbis} = await import('dex-abi/errors');

        allContractRecs = Object.entries(errorAbis).map(
            ([name, abi]) => ({name, abi: abi as unknown as Abi}),
        );

        // Selector index: viem types toFunctionSelector as AbiFunction, but the error selector is calculated by the same function (verified with encodeErrorResult). Since errorAbis only contains error fragments, a filter is not needed.
        type SelectorArg = Parameters<typeof toFunctionSelector>[0]
        errorSelectorIndex = new Map(
            allContractRecs.flatMap(({name, abi}) =>
                (abi as unknown as ReadonlyArray<AbiErrorItem>)
                    .map((item) => [
                        toFunctionSelector(item as unknown as SelectorArg),
                        {interfaceName: name, name: item.name},
                    ] as const),
            ),
        );

        if (isDevOrCiEnv(appConfig.env)) {
            console.groupCollapsed('DEX Contract Errors Decoding');
            console.log('Contract Count:', allContractRecs.length);
            console.log('Contract Names:', allContractRecs.map((it) => it.name));
            console.groupEnd();
        }
    })());

export const getAllAbis = async <ABI extends Abi = Abi>(): Promise<ABI> => {
    // Full merged ABI is rarely needed (dev utilities, decodeFunctionData).
    // We load it LAZILY from the 'dex-abi' root: the bundler does not include the full-ABI barrel
    // even in the decoder's lazy chunk unless getAllAbis() is called.
    allAbisPromise ??= (async () => {
        const {allAbis} = await import('dex-abi');

        return allAbis;
    })();

    return (await allAbisPromise) as ABI;
};

const revertArgsToMap = (
    inputs?: ReadonlyArray<{ name?: string }>,
    args?: ReadonlyArray<any>
): Record<string, any> | undefined => {
    return inputs?.reduce(
        (dict, param, iParam) => {
            if (param.name) {
                dict[param.name] = args?.[iParam];
            }

            return dict;
        },
        {} as Record<string, any>
    );
};

/**
 * Recursively extracts the raw revert data (hex string) from an error chain.
 *
 * This is necessary when the error might come from a different copy of Viem
 * (e.g., bundled inside a web component like Dynamic), where `instanceof`
 * checks would fail. By looking for the standard JSON-RPC revert indicator
 * (`data.code === 3`) or a known name of the error, it works across multiple Viem versions.
 */
function getRevertData(error: unknown): Hex | undefined {
    if (!(error instanceof Error)) return undefined;

    // viem: ContractFunctionRevertedError.raw - RAW revert data
    if ((error as BaseError).name === 'ContractFunctionRevertedError') {
        return (error as ContractFunctionRevertedError).raw;
    }

    const data = (error as any)?.data;

    if (data && typeof data === 'object' && data.code === 3) {
        return data.data ?? undefined; // hex string
    }

    if ('cause' in error) {
        return getRevertData(error.cause);
    }

    return undefined;
}

/**
 * Recursively extracts the raw revert data (hex string) from an error chain.
 *
 * This is necessary when the error might come from a different copy of Viem
 * (e.g., bundled inside a web component like Dynamic), where `instanceof`
 * checks would fail. By looking for the known name of the error,
 * it works across multiple Viem versions.
 */
function getErrorInstanceByName<T = unknown>(error: unknown, name: string): T | undefined {
    if (!(error instanceof Error)) return undefined;

    if ((error as BaseError).name === name) {
        return error as T;
    }

    if ('cause' in error) {
        return getErrorInstanceByName(error.cause, name);
    }

    return undefined;
}

function getErrorDetailsString(error: unknown): string | undefined {
    if (!(error instanceof Error)) return undefined;

    const details = (error as BaseError).details;
    if (details && typeof details === 'string') {
        return details;
    }

    if ('cause' in error) {
        return getErrorDetailsString(error.cause);
    }

    return undefined;
}

function getViemRevertReason(error: unknown): string | undefined {
    const e = getErrorInstanceByName<ContractFunctionRevertedError>(error, 'ContractFunctionRevertedError');

    return e?.reason || undefined;
}

export function getViemRevertFallbackMessage(error: unknown): string | undefined {
    const reason = getViemRevertReason(error);
    if (reason) return reason;

    const details = getErrorDetailsString(error);
    if (details) return details;

    return undefined;
}

export async function parseRevertErrorViem(error: unknown): Promise<ParsedReverDataViem | undefined> {
    const data = getRevertData(error);

    if (data && typeof data === 'string') {
        return decodeReverErrorDataViem(data);
    }

    return undefined;
}

export async function decodeReverErrorDataViem(revertData: `0x${string}`): Promise<ParsedReverDataViem | undefined> {
    await initErrorDecoder();

    // >= 10: '0x' + 4 byte selector - correct revert data
    // for errors WITHOUT arguments (encodeErrorResult gives exactly 10 characters)
    if (revertData.length >= 10) {
        const selector = revertData.slice(0, 10) as `0x${string}`;

        // 1) full parsing: name + encoded arguments
        for (const rec of allContractRecs) {
            try {
                const {abiItem, args, errorName} = decodeErrorResult({
                    abi:  rec.abi,
                    data: revertData
                });

                // abiItem is virtually always an error, but for a generic Abi,
                // viem types it as a broad union - we narrow it down ourselves
                const errorAbiItem = abiItem as AbiErrorItem;

                return {
                    interfaceName: rec.name,
                    name:          errorName,
                    args:          args ?? [],
                    argsMap:       revertArgsToMap(errorAbiItem.inputs, args),
                    selector,
                } satisfies ParsedReverDataViem;
            } catch {
                // selector does not belong to this contract - next
            }
        }

        // 2) fallback: selector is known, but data is abbreviated -
        //    error WITH arguments, but revert data contains only the selector
        //    (analogous to the old ethers fragment branch)
        const rec = errorSelectorIndex.get(selector);

        if (rec) {
            return {
                interfaceName: rec.interfaceName,
                name:          rec.name,
                args:          [],
                argsMap:       undefined,
                selector,
            } satisfies ParsedReverDataViem;
        }
    }

    return undefined;
}

// Debug helpers (dev/ci only)
if (isDevOrCiEnv(appConfig.env) && typeof window !== 'undefined') {
    (window as any).decodeReverErrorData = decodeReverErrorDataViem;
    (window as any).decodeFn = async (d: string) => {
        // Full merged ABI - lazy, only on demand from devtools console
        const allAbis = await getAllAbis();

        return decodeFunctionData({
            abi:  allAbis,
            data: d as Hex
        });
    };
}
